import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  useToast,
  VStack,
  CircularProgress,
  Icon,
  Center,
  Tooltip,
  IconButton,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import {
  RiCheckboxCircleLine,
  RiCloseCircleLine,
  RiErrorWarningLine,
} from 'react-icons/ri';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import AddressForm, {
  addressValidation,
} from '../../../../../components/AddressForm';
import { AvatarInput } from '../../../../../components/Form/AvatarInput';
import { DatePicker } from '../../../../../components/Form/DatePicker';
import { InternationalPhoneInput } from '../../../../../components/Form/InternationalPhoneInput';
import { MaskedInput } from '../../../../../components/Form/MaskedInput';
import {
  ReactSelect,
  SelectOption,
} from '../../../../../components/Form/ReactSelect';
import { UserAssignmentModal } from '../../../../../components/UserAssignmentModal';
import { useAuth } from '../../../../../hooks/auth';
import { IAddressBase } from '../../../../../models/address';
import { UserAvatarStatus } from '../../../../../models/users';
import { showFeatureGroupsByKeyService } from '../../../../../services/FeatureGroups/ShowFeatureGroupsByKeyService';
import { listUserCategoriesService } from '../../../../../services/UserCategories/ListUserCategoriesService';
import { checkExistingUsersService } from '../../../../../services/Users/CheckExistingUsersService';
import { checkUsersFaceService } from '../../../../../services/Users/CheckUsersFaceService';
import { createUserAssignmentsService } from '../../../../../services/Users/CreateUserAssignmentsService';
import { createUsersService } from '../../../../../services/Users/CreateUsersService';
import { updateUserAvatarsService } from '../../../../../services/Users/UpdateUserAvatarsService';
import {
  validateBRPhone,
  validateCpf,
} from '../../../../../utils/documentValidation';
import { translateError } from '../../../../../utils/errors';
import { DefaultLayout } from '../../../_layout/DefaultLayout';
import AvatarStatusCard from '../../components/AvatarStatusCard';
import FaceIdStatusTag from '../../components/FaceIDStatusTag';

type cpfAvailability =
  | 'available'
  | 'cpf-already-in-use'
  | 'cpf-already-in-use-on-venture';

type NewMemberFormData = {
  address?: IAddressBase;
  bio?: string;
  birthDate: Date;
  categoryId: string;
  cpf?: string;
  email: string;
  healthCheck?: Date;
  name: string;
  phone?: string;
};

const registerMemberFormSchema = Yup.object().shape({
  address: addressValidation
    .nullable()
    .transform((value, originalValue) =>
      !!originalValue &&
      Object.values<string | undefined>(originalValue).some(
        (val) => !!val?.length,
      )
        ? originalValue
        : null,
    ),
  bio: Yup.string()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  birthDate: Yup.date().nullable().required('Data de nascimento requerida'),
  categoryId: Yup.string().nullable().required('Categoria requerida'),
  cpf: Yup.string()
    .length(11, 'Valor inválido, requerido 11 dígitos')
    .test('is-valid', 'CPF inválido', (value) => validateCpf(value))
    .nullable()
    .transform((_, originalValue) =>
      originalValue.replace(/\D/g, '').length
        ? originalValue.replace(/\D/g, '')
        : null,
    ),
  email: Yup.string()
    .email('E-mail inválido')
    .required('E-mail requerido')
    .transform((value) => value.toLowerCase()),
  healthCheck: Yup.date()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  name: Yup.string()
    .required('Nome requerido')
    .matches(/^[a-zA-ZÀ-ÿ\s]+$/, 'Nome inválido'),
  phone: Yup.string()
    .test('is-valid', 'Telefone inválido', (value) => validateBRPhone(value))
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
});

export const MemberRegister = (): JSX.Element => {
  const { goBack, push } = useHistory();
  const toast = useToast();

  const { venture: authenticatedVenture } = useAuth();

  const [avatar, setAvatar] = useState<File>();
  const [avatarUrl, setAvatarUrl] = useState<string>();
  const [isCheckingAvatar, setIsCheckingAvatar] = useState(false);
  const [avatarStatus, setAvatarStatus] = useState<UserAvatarStatus>(
    UserAvatarStatus.EMPTY,
  );
  const [loading, setLoading] = useState(false);
  const [cpfAvailability, setCpfAvailability] = useState<cpfAvailability>();
  const [featureGroupUseFaceId, setFeatureGroupUseFaceId] = useState(false);
  const [categorySelectOptions, setcategorySelectOptions] = useState<
    SelectOption[]
  >([]);
  const [isAssignConfirmModalVisible, setIsAssignConfirmModalVisible] =
    useState(false);

  const formMethods = useForm({
    resolver: yupResolver(registerMemberFormSchema),
  });

  const { register, handleSubmit, formState, control, setValue, watch } =
    formMethods;

  const { errors } = formState;

  const currentCpf = watch('cpf');

  useEffect(() => {
    async function loadCategories(ventureId: string): Promise<void> {
      try {
        const userCategories = await listUserCategoriesService(ventureId);

        setcategorySelectOptions(
          userCategories.map((userCategory) => ({
            label: userCategory.title,
            value: userCategory.id,
          })),
        );
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: 'Falha ao carregar dados',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao carregar as categorias disponíveis, tente novamente.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    }

    if (authenticatedVenture) {
      loadCategories(authenticatedVenture.id);
    }
  }, [authenticatedVenture, toast]);

  useEffect(() => {
    async function loadFeatureGroup(): Promise<void> {
      const featureGroup = await showFeatureGroupsByKeyService('MEMBER');

      if (featureGroup) {
        setFeatureGroupUseFaceId(featureGroup.useFaceId);
      }
    }

    loadFeatureGroup();
  }, []);

  useEffect(() => {
    setValue('address', null);
  }, [setValue]);

  const checkUserAvatar = useCallback(
    async (file: File) => {
      setIsCheckingAvatar(true);

      try {
        const formData = new FormData();
        formData.append('face', file);

        const { avatarStatus: verifiedAvatarStatus } =
          await checkUsersFaceService({
            faceData: formData,
          });

        setAvatarStatus(verifiedAvatarStatus);

        if (verifiedAvatarStatus === UserAvatarStatus.VALID) {
          toast({
            title: 'Foto validada',
            description: 'A foto foi validada com sucesso',
            status: 'success',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      } catch (err) {
        setAvatarStatus(UserAvatarStatus.NOT_VERIFIED);

        toast({
          title: 'Foto não validada',
          description: 'Ocorreu um erro ao validar a foto',
          status: 'error',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });
      } finally {
        setIsCheckingAvatar(false);
      }
    },
    [toast],
  );

  useEffect(() => {
    async function checkUserDocument(cpf: string): Promise<void> {
      setLoading(true);

      try {
        await checkExistingUsersService({
          cpf,
          featureGroupKey: 'MEMBER',
        });

        setCpfAvailability('available');
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          if (err.response?.data.message === 'cpf-already-in-use') {
            setCpfAvailability('cpf-already-in-use');
            setIsAssignConfirmModalVisible(true);

            return;
          }

          if (err.response?.data.message === 'cpf-already-in-use-on-venture') {
            setCpfAvailability('cpf-already-in-use-on-venture');

            return;
          }

          toast({
            title: 'Falha na validação',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao validar o documento, tente novamente.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      } finally {
        setLoading(false);
      }
    }

    const cpfDigits = currentCpf?.replace(/\D/g, '');

    if (!!cpfDigits?.length && validateCpf(cpfDigits)) {
      checkUserDocument(cpfDigits);
    }
  }, [currentCpf, toast]);

  useEffect(() => {
    if (!!avatar && !!featureGroupUseFaceId) {
      checkUserAvatar(avatar);
    }
  }, [avatar, checkUserAvatar, featureGroupUseFaceId]);

  const handleChangeAvatar = useCallback((file: File) => {
    setAvatar(file);
    setAvatarUrl(URL.createObjectURL(file));
  }, []);

  const handleDeleteAvatar = useCallback(() => {
    setAvatarStatus(UserAvatarStatus.EMPTY);
    setAvatar(undefined);
    setAvatarUrl(undefined);
  }, []);

  const handleToggleConfirmationModal = useCallback(() => {
    setIsAssignConfirmModalVisible((prevState) => !prevState);
  }, []);

  const handleAssignConfirm = useCallback(
    async (categoryId: string) => {
      const member = await createUserAssignmentsService({
        categoryId,
        cpf: currentCpf,
        featureGroupKey: 'MEMBER',
      });

      setCpfAvailability('available');

      handleToggleConfirmationModal();

      push('/members/details', { memberId: member.id });
    },
    [currentCpf, handleToggleConfirmationModal, push],
  );

  const handleNewMember: SubmitHandler<NewMemberFormData> = useCallback(
    async ({
      address,
      bio,
      birthDate,
      categoryId,
      cpf,
      email,
      healthCheck,
      name,
      phone,
    }) => {
      if (!authenticatedVenture) {
        return;
      }

      try {
        const member = await createUsersService({
          address,
          bio,
          birthDate,
          categoriesId: [categoryId],
          cpf,
          email,
          healthCheck,
          name,
          phone,
          featureGroupKey: 'MEMBER',
          venturesId: [authenticatedVenture?.id],
        });

        if (avatar) {
          const formData = new FormData();

          formData.append('avatar', avatar);

          await updateUserAvatarsService({
            avatarData: formData,
            userId: member.id,
          });
        }

        toast({
          title: 'Cadastrado com sucesso',
          description: 'O membro foi cadastrado corretamente.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });

        push('/members/details', { memberId: member.id });
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          if (err.response?.data.message === 'cpf-already-in-use') {
            setCpfAvailability('cpf-already-in-use');
            setIsAssignConfirmModalVisible(true);

            return;
          }

          if (err.response?.data.message === 'cpf-already-in-use-on-venture') {
            setCpfAvailability('cpf-already-in-use-on-venture');

            return;
          }

          toast({
            title: 'Falha no cadastro',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao cadastrar o membro, tente novamente.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    },
    [authenticatedVenture, avatar, toast, push],
  );

  const renderCpfValidationIcon = (): JSX.Element => {
    if (loading) {
      return (
        <Tooltip label="Verificando disponibilidade">
          <Center h="48px">
            <CircularProgress isIndeterminate color="teal" size="1.5rem" />
          </Center>
        </Tooltip>
      );
    }

    if (!loading && cpfAvailability === 'available') {
      return (
        <Tooltip label="Disponível">
          <Center h="48px">
            <Icon as={RiCheckboxCircleLine} fontSize="24" color="green.500" />
          </Center>
        </Tooltip>
      );
    }

    if (!loading && cpfAvailability === 'cpf-already-in-use') {
      return (
        <Tooltip label="Em uso">
          <Center h="48px" w="24px">
            <IconButton
              aria-label="toggle-assignment"
              icon={<Icon as={RiErrorWarningLine} />}
              fontSize="24"
              variant="unstyled"
              color="yellow.500"
              onClick={handleToggleConfirmationModal}
              display="flex"
              alignItems="center"
              h="48px"
              w="24px"
            />
          </Center>
        </Tooltip>
      );
    }

    if (!loading && cpfAvailability === 'cpf-already-in-use-on-venture') {
      return (
        <Tooltip label="Indisponível">
          <Center h="48px">
            <Icon as={RiCloseCircleLine} fontSize="24" color="red.500" />
          </Center>
        </Tooltip>
      );
    }

    return <></>;
  };

  return (
    <DefaultLayout>
      <UserAssignmentModal
        categoryOptions={categorySelectOptions}
        isOpen={isAssignConfirmModalVisible}
        onClose={handleToggleConfirmationModal}
        onConfirm={handleAssignConfirm}
      />

      {/* <MemberCategoryModal
        isOpen={isMemberCategoryModalVisible}
        onClose={() => setIsMemberCategoryModalVisible(false)}
        onConfirm={(categories) => {
          setCurrentCategories(categories);
          setIsMemberCategoryModalVisible(false);
        }}
        defaultCategories={[]}
      /> */}

      <FormProvider {...formMethods}>
        <Box
          as="form"
          flex="1"
          borderRadius={8}
          bg="white"
          p="8"
          onSubmit={handleSubmit(handleNewMember)}
        >
          <Heading size="lg" fontWeight="normal">
            Cadastrar membro
          </Heading>

          <Divider my="6" borderColor="gray.300" />

          <Flex direction="column" align="center" mb="8">
            <Box mb={6} alignSelf="flex-end">
              <FaceIdStatusTag
                avatarChanged={!!featureGroupUseFaceId && !!avatar}
                avatarStatus={avatarStatus}
              />
            </Box>

            <AvatarInput
              avatarUrl={avatarUrl}
              onChange={handleChangeAvatar}
              onDelete={handleDeleteAvatar}
              showWebcamButton
            />

            <Box mt={6}>
              <AvatarStatusCard avatarStatus={avatarStatus} />
            </Box>

            {!!avatar &&
              (avatarStatus === UserAvatarStatus.NOT_VERIFIED ||
                isCheckingAvatar) && (
                <Box mt={3}>
                  <Button
                    isDisabled={!avatar}
                    isLoading={isCheckingAvatar}
                    loadingText="Validando foto..."
                    colorScheme="blue"
                    onClick={() => checkUserAvatar(avatar)}
                  >
                    Validar foto
                  </Button>
                </Box>
              )}
          </Flex>

          <VStack spacing="8">
            <MaskedInput
              label="Nome completo"
              error={errors.name}
              {...register('name')}
            />

            <Grid
              templateColumns={[
                'repeat(1, 1fr)',
                'repeat(1, 1fr)',
                'repeat(12, 1fr)',
                'repeat(12, 1fr)',
                'repeat(12, 1fr)',
              ]}
              columnGap="4"
              rowGap="8"
              width="100%"
            >
              <GridItem colSpan={[12, 12, 6]}>
                <HStack alignItems="flex-end" spacing="2">
                  <MaskedInput
                    label="CPF"
                    mask="cpf"
                    error={errors.cpf}
                    {...register('cpf')}
                  />

                  {renderCpfValidationIcon()}
                </HStack>
              </GridItem>

              <GridItem colSpan={[12, 12, 6]}>
                <DatePicker
                  label="Data de nascimento"
                  isClearable={false}
                  maxDate={new Date()}
                  control={control}
                  error={errors.birthDate}
                  {...register('birthDate')}
                />
              </GridItem>

              <GridItem colSpan={[12, 12, 6]}>
                <InternationalPhoneInput
                  label="Telefone"
                  name="phone"
                  control={control}
                  error={errors.phone}
                />
              </GridItem>

              <GridItem colSpan={[12, 12, 6]}>
                <MaskedInput
                  label="E-mail"
                  type="email"
                  textTransform="lowercase"
                  error={errors.email}
                  {...register('email')}
                />
              </GridItem>

              <GridItem colSpan={[12, 12, 6]}>
                <DatePicker
                  label="Exame médico"
                  maxDate={new Date()}
                  control={control}
                  error={errors.healthCheck}
                  {...register('healthCheck')}
                />
              </GridItem>

              <GridItem colSpan={[12, 12, 6]}>
                <ReactSelect
                  label="Categoria"
                  name="categoryId"
                  options={categorySelectOptions}
                  control={control}
                  error={errors.categoryId}
                />
              </GridItem>
            </Grid>

            <MaskedInput
              label="Biografia"
              as="textarea"
              minHeight="160px"
              resize="none"
              py="2"
              error={errors.bio}
              {...register('bio')}
            />
          </VStack>

          <Divider my="6" borderColor="gray.300" />

          <AddressForm />

          <Flex mt="12" justify="flex-end">
            <ButtonGroup>
              <Button colorScheme="blackAlpha" onClick={goBack}>
                Cancelar
              </Button>

              <Button
                type="submit"
                colorScheme="green"
                isLoading={formState.isSubmitting}
              >
                Salvar
              </Button>
            </ButtonGroup>
          </Flex>
        </Box>
      </FormProvider>
    </DefaultLayout>
  );
};
